import React from "react";
import { Badge, Box, HStack, Text, Tooltip, VStack } from "@chakra-ui/react";
import { HAPPY_PATH, PHASES } from "../flow/flowGraph";
import type { FlowContext } from "../flow/types";

interface Props {
  value: string;
  onChange: (stepId: string) => void;
  disabled: boolean;
  ctx?: Pick<FlowContext, "config" | "supplier" | "flags" | "orcaUser">;
  /** Steps already completed in the current run (for "continue to" mode). */
  completedIds?: string[];
}

const TargetSelector: React.FC<Props> = ({ value, onChange, disabled, ctx, completedIds = [] }) => {
  const selectedIdx = HAPPY_PATH.findIndex((s) => s.id === value);
  return (
    <Box bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="lg" p={4} opacity={disabled ? 0.7 : 1} transition="opacity 0.2s">
      <HStack justify="space-between" mb={3}>
        <Text fontWeight="700" fontSize="sm">
          Stop at
        </Text>
        <Text fontSize="10px" color="gray.400">
          all prior steps run automatically
        </Text>
      </HStack>
      <VStack align="stretch" spacing={3}>
        {PHASES.map((phase) => (
          <Box key={phase.id}>
            <Text fontSize="10px" fontWeight="700" textTransform="uppercase" letterSpacing="0.08em" color="gray.500" mb={1}>
              {phase.label}
            </Text>
            <VStack align="stretch" spacing={0.5}>
              {phase.stepIds.map((id) => {
                const step = HAPPY_PATH.find((s) => s.id === id)!;
                const idx = HAPPY_PATH.indexOf(step);
                const selected = id === value;
                const included = idx <= selectedIdx;
                const done = completedIds.includes(id);
                const skipReason = ctx && step.shouldSkip ? step.shouldSkip(ctx as FlowContext) : null;
                return (
                  <Tooltip key={id} label={step.description} placement="right" openDelay={400} hasArrow>
                    <HStack
                      px={2}
                      py={1}
                      borderRadius="md"
                      cursor={disabled || done ? "default" : "pointer"}
                      bg={selected ? "blue.500" : included ? "blue.50" : "transparent"}
                      color={selected ? "white" : "gray.700"}
                      _hover={!disabled && !done && !selected ? { bg: "blue.100" } : undefined}
                      onClick={() => !disabled && !done && onChange(id)}
                      transition="background 0.15s"
                      opacity={done ? 0.55 : 1}
                    >
                      <Text fontSize="xs" flex={1} textDecoration={done ? "line-through" : undefined}>
                        {step.targetLabel}
                      </Text>
                      {step.kind === "event" && (
                        <Badge colorScheme={selected ? "whiteAlpha" : "orange"} variant={selected ? "solid" : "outline"} fontSize="8px">
                          event
                        </Badge>
                      )}
                      {step.optional && (
                        <Badge colorScheme={selected ? "whiteAlpha" : "gray"} variant={selected ? "solid" : "outline"} fontSize="8px">
                          optional
                        </Badge>
                      )}
                      {skipReason && !selected && (
                        <Badge colorScheme="gray" variant="subtle" fontSize="8px" title={skipReason}>
                          will skip
                        </Badge>
                      )}
                    </HStack>
                  </Tooltip>
                );
              })}
            </VStack>
          </Box>
        ))}
      </VStack>
    </Box>
  );
};

export default TargetSelector;
